import { useState } from 'react';
import { features, reviews } from '@/mocks/comicData';

const CONTACT_URL = 'https://readdy.ai/api/form/da1gbpomlpfrg29fphlg';

export default function About() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // 蜜罐检测：非空则静默当作成功，不提交
    const honey = String(formData.get('company_alt') || '').trim();
    if (honey) {
      setStatus('success');
      setSuccessMsg('已收到你的留言，我们会尽快回复你。');
      return;
    }
    formData.delete('company_alt');

    setStatus('loading');
    setFormError('');
    try {
      const payload = new URLSearchParams(formData as unknown as Record<string, string>);
      const res = await fetch(CONTACT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText || '';
      const isSpam =
        parsed?.code !== 'OK' &&
        (serverMsg.toLowerCase().includes('spam') || responseText.includes('form data is spam'));

      if (res.ok && parsed?.code === 'OK' && !isSpam) {
        form.reset();
        setStatus('success');
        setSuccessMsg('已收到你的留言，我们会尽快回复你。');
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后再试。');
    }
  };

  return (
    <section id="about" className="w-full bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
          {/* 左：品牌介绍与口碑 */}
          <div>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600">
              <i className="ri-information-line"></i>关于虫虫
            </span>
            <h2 className="mt-2 font-heading text-3xl text-foreground-950 md:text-4xl">
              我们，为"找书"而生
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base">
              虫虫漫画是一个专注国漫、日漫的<b>分类大全</b>内容门户。我们深知漫迷们找书时最大的痛点，
              是"不知道去哪找、分类太乱"。所以虫虫把作品按<b>题材类型</b>仔细整理——热血、恋爱、奇幻、
              悬疑、搞笑、都市、历史、科幻……无论是正在追更的热门<b>连载</b>，还是值得回味的<b>经典全本</b>，
              都能在对应分类里快速找到。
            </p>
            <p className="mt-3 text-sm leading-7 text-foreground-700 md:text-base">
              我们坚持<b>内容整洁、检索高效、推荐靠谱</b>三个原则，让每一位点开虫虫漫画的人，都能把时间花在阅读上，而不是花在翻找上。
            </p>

            <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {features.map((f) => (
                <div
                  key={f.title}
                  className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-600">
                    <i className={f.icon}></i>
                  </span>
                  <div>
                    <h3 className="text-sm font-bold text-foreground-950">{f.title}</h3>
                    <p className="mt-1 text-xs leading-5 text-foreground-600">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 右：口碑评价 + 联系表单 */}
          <div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h3 className="flex items-center gap-2 font-heading text-xl text-foreground-950">
                <i className="ri-heart-3-line text-primary-500"></i> 读者口碑
              </h3>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {reviews.map((r) => (
                  <figure key={r.name} className="rounded-lg bg-accent-100/70 p-4">
                    <div className="flex items-center gap-1 text-accent-600">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-xs"></i>
                      ))}
                    </div>
                    <blockquote className="mt-2 text-xs leading-5 text-foreground-700">“{r.text}”</blockquote>
                    <figcaption className="mt-3 text-xs text-foreground-500">
                      <span className="font-bold text-foreground-800">{r.name}</span>
                      <span> · {r.role}</span>
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>

            <form
              id="contact-form"
              data-readdy-form="联系我们表单"
              onSubmit={onSubmit}
              className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
            >
              <h3 className="flex items-center gap-2 font-heading text-xl text-foreground-950">
                <i className="ri-mail-send-line text-primary-500"></i> 联系我们 / 推荐漫画
              </h3>
              <p className="mt-2 text-xs leading-5 text-foreground-600">
                有想看的漫画却找不到？或对虫虫漫画有任何建议，欢迎留言，我们持续收录更新。
              </p>

              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="text-xs font-semibold text-foreground-700">
                    你的称呼
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼你"
                    className="mt-1.5 w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-300"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="text-xs font-semibold text-foreground-700">
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收回复"
                    className="mt-1.5 w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-300"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label htmlFor="contact-msg" className="text-xs font-semibold text-foreground-700">
                  留言内容
                </label>
                <textarea
                  id="contact-msg"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  placeholder="想推荐的漫画、想看的题材，或任何建议…（500字以内）"
                  className="mt-1.5 w-full resize-none rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-300"
                ></textarea>
                <p className="mt-1 text-right text-[11px] text-foreground-400">最多 500 字</p>
              </div>
              {/* 反垃圾蜜罐 */}
              <div className="footnote-skip" aria-hidden="true">
                <label>
                  公司
                  <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" readOnly />
                </label>
              </div>

              {status === 'success' && (
                <p className="mt-4 flex items-center gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-sm text-accent-800">
                  <i className="ri-checkbox-circle-line"></i>
                  {successMsg}
                </p>
              )}
              {status === 'error' && (
                <p className="mt-4 flex items-center gap-2 rounded-md bg-primary-100 px-3 py-2.5 text-sm text-primary-800">
                  <i className="ri-error-warning-line"></i>
                  {formError}
                </p>
              )}

              <button
                type="submit"
                disabled={status === 'loading'}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {status === 'loading' ? '发送中…' : '提交留言'}
                {status !== 'loading' && <i className="ri-send-plane-line"></i>}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}